<template>
  <section class="pickup-google-sheets-config">
    <h4 class="pickup-google-sheets-config__title">Configuración de Google Sheets de Retiros</h4>
    <p class="pickup-google-sheets-config__hint">
      Se sincronizará automáticamente el detalle de retiros del día (00:00 hora de Chile)
      al archivo de Google Sheets configurado.
    </p>

    <div v-if="!credentialsConfigured" class="pickup-google-sheets-config__alert pickup-google-sheets-config__alert--warning">
      <strong>Credenciales no configuradas.</strong>
      Las credenciales de la cuenta de servicio deben estar definidas en el servidor
      (<code>GOOGLE_SHEETS_CLIENT_EMAIL</code> y <code>GOOGLE_SHEETS_PRIVATE_KEY</code>).
      Comparte el archivo de Google Sheets con el email de la cuenta de servicio.
    </div>

    <form class="pickup-google-sheets-config__form" @submit.stop.prevent="onSubmit">
      <AppField
        label="Archivo de Google Sheets"
        description="ID del archivo o su URL completa. Ej: https://docs.google.com/spreadsheets/d/1AbC.../edit"
      >
        <AppInput
          v-model="form.spreadsheetId"
          maxlength="500"
          placeholder="ID o URL del Google Sheet"
        />
      </AppField>

      <AppField
        label="Nombre de la hoja"
        description="Pestaña del archivo donde se escribirán los datos."
      >
        <AppInput
          v-model="form.sheetName"
          maxlength="200"
          placeholder="Sheet1"
        />
      </AppField>

      <AppField label="Habilitado">
        <AppCheckbox v-model="form.enabled">
          Sincronizar automáticamente cada día
        </AppCheckbox>
      </AppField>

      <div class="pickup-google-sheets-config__actions">
        <AppButton
          variant="primary"
          type="submit"
          :disabled="loading || !credentialsConfigured"
        >
          Guardar
        </AppButton>
        <AppButton
          variant="secondary"
          outline
          type="button"
          :disabled="testing || !form.spreadsheetId || !credentialsConfigured"
          @click="onTest"
        >
          Probar conexión
        </AppButton>
        <AppButton
          v-if="debugEnabled"
          variant="danger"
          outline
          type="button"
          :disabled="syncing || !credentialsConfigured"
          @click="onSync"
        >
          {{ syncing ? 'Sincronizando...' : 'Forzar sincronización' }}
        </AppButton>
      </div>
      <p v-if="debugEnabled" class="pickup-google-sheets-config__debug-hint">
        Modo debug: este botón fuerza la sincronización del día anterior sin esperar a las 00:00.
      </p>
    </form>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import { isDebugEnabled } from '../../utils/debugUtils'
import AppField from '../UI/AppField'
import AppInput from '../UI/AppInput'
import AppButton from '../UI/AppButton'
import AppCheckbox from '../UI/AppCheckbox'

export default {
  name: 'PickupGoogleSheetsConfig',
  components: {
    AppField,
    AppInput,
    AppButton,
    AppCheckbox
  },
  data () {
    return {
      loading: false,
      testing: false,
      syncing: false,
      debugEnabled: false,
      credentialsConfigured: false,
      form: {
        spreadsheetId: '',
        sheetName: 'Sheet1',
        enabled: false
      }
    }
  },
  methods: {
    ...mapActions([
      'getPickupGoogleSheetsConfig',
      'updatePickupGoogleSheetsConfig',
      'testPickupGoogleSheetsConnection',
      'syncPickupGoogleSheets'
    ]),
    async fetchConfig () {
      try {
        const response = await this.getPickupGoogleSheetsConfig()
        const config = response.data
        this.credentialsConfigured = !!config.credentialsConfigured
        this.form.spreadsheetId = config.spreadsheetId || ''
        this.form.sheetName = config.sheetName || 'Sheet1'
        this.form.enabled = !!config.enabled
      } catch (error) {
        this.$toast.danger('Error al cargar la configuración')
      }
    },
    async onSubmit () {
      this.loading = true
      try {
        await this.updatePickupGoogleSheetsConfig({ data: this.form })
        this.$toast.success('Configuración guardada exitosamente')
      } catch (error) {
        this.$toast.danger(this.getErrorMessage(error))
      } finally {
        this.loading = false
      }
    },
    async onTest () {
      this.testing = true
      try {
        const response = await this.testPickupGoogleSheetsConnection({ data: this.form })
        this.$toast.success(response.data.message || 'Conexión exitosa')
      } catch (error) {
        this.$toast.danger(this.getErrorMessage(error))
      } finally {
        this.testing = false
      }
    },
    async onSync () {
      this.syncing = true
      try {
        const response = await this.syncPickupGoogleSheets({ data: {} })
        const statusMessages = {
          synced: `Sincronización exitosa: ${response.data.rowCount} filas escritas`,
          already_logged: 'La fecha ya estaba sincronizada en la hoja',
          disabled: 'La sincronización automática está deshabilitada',
          not_configured: 'El archivo de Google Sheets no está configurado',
          no_data: 'No hay retiros para la fecha'
        }
        const message = statusMessages[response.data.status] || 'Sincronización forzada'
        this.$toast.success(message)
      } catch (error) {
        this.$toast.danger(this.getErrorMessage(error))
      } finally {
        this.syncing = false
      }
    },
    getErrorMessage (error) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message
      }
      if (error && error.message) {
        return error.message
      }
      return 'Ocurrió un error'
    }
  },
  mounted () {
    this.debugEnabled = isDebugEnabled()
    this.fetchConfig()
  }
}
</script>

<style scoped>
.pickup-google-sheets-config {
  display: grid;
  gap: 1rem;
}

.pickup-google-sheets-config__title {
  margin: 0;
}

.pickup-google-sheets-config__hint {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.pickup-google-sheets-config__alert {
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.pickup-google-sheets-config__alert--warning {
  background-color: #fff3cd;
  border: 1px solid #ffe69c;
  color: #664d03;
}

.pickup-google-sheets-config__alert code {
  background-color: rgba(0, 0, 0, 0.08);
  padding: 0.1rem 0.3rem;
  border-radius: 0.2rem;
  font-size: 0.8em;
}

.pickup-google-sheets-config__form {
  display: grid;
  gap: 0.5rem;
}

.pickup-google-sheets-config__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pickup-google-sheets-config__debug-hint {
  margin: 0;
  color: #9a3412;
  font-size: 0.8rem;
}
</style>
